import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="library-search"
export default class extends Controller {
  static targets = ["input", "results", "collectionGroups", "emptyState", "clearButton"]
  static values = {
    url: String,
    debounceMs: { type: Number, default: 300 },
    initialQuery: { type: String, default: "" }
  }

  connect() {
    this.timeout = null
    
    // Restore search state from initial query if present
    if (this.initialQueryValue && this.hasInputTarget) {
      this.inputTarget.value = this.initialQueryValue
      this.toggleClearButton(true)
      this.performSearch(this.initialQueryValue)
    }
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  search(event) {
    if (!this.hasInputTarget) {
      return
    }
    const query = this.inputTarget.value.trim()

    // Clear any pending timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Update clear button visibility
    this.toggleClearButton(query.length > 0)

    // If query is empty, show collection groups and hide results
    if (query.length === 0) {
      this.showCollectionGroups()
      this.clearSessionQuery()
      return
    }

    // Debounce the search request
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceMsValue)
  }

  async performSearch(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        }
      })

      if (response.ok) {
        const html = await response.text()
        this.resultsTarget.innerHTML = html
        this.showResults()
      } else {
        this.resultsTarget.innerHTML = `<div class="text-center py-8 text-gray-500">Search unavailable (${response.status}). Please try again.</div>`
        this.showResults()
      }
    } catch (error) {
      this.resultsTarget.innerHTML = `<div class="text-center py-8 text-gray-500">Search error occurred. Please try again.</div>`
      this.showResults()
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.toggleClearButton(false)
    this.showCollectionGroups()
    this.clearSessionQuery()
    this.inputTarget.focus()
  }

  clearSessionQuery() {
    // Clear the search query from the session
    fetch(this.urlValue + "?clear_session=true", {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      }
    }).catch(() => {
      // Silently ignore errors when clearing session
    })
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
    if (this.hasCollectionGroupsTarget) {
      this.collectionGroupsTarget.classList.add("hidden")
    }
  }

  showCollectionGroups() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    if (this.hasCollectionGroupsTarget) {
      this.collectionGroupsTarget.classList.remove("hidden")
    }
  }

  toggleClearButton(show) {
    if (this.hasClearButtonTarget) {
      if (show) {
        this.clearButtonTarget.classList.remove("hidden")
      } else {
        this.clearButtonTarget.classList.add("hidden")
      }
    }
  }
}
